import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup';

import { postWithAuth } from "protocol-setup/redux/makeFetch";
import { requestValidationPath } from "protocol-setup/redux/constants";
import { useHistory } from "react-router-dom";
import FormRadioGroup from "protocol-setup/components/FormComponents/FormRadioGroup";
import FormSelect from "protocol-setup/components/FormComponents/FormSelect";
import { useProtocolLOVs, useProtocolSetupData, useRequestBarData } from "protocol-setup/redux/useLovs";
import text from "protocol-setup/constants/text";
import errorMessages from "protocol-setup/constants/errorMessages";
import { useSetupPermission } from "protocol-setup/helpers";
import ProtectionElements from "protocol-setup/helpers/ProtectionElement";
import { requestStatusOptions } from "./lov";

export const formSchema = yup.object().shape({
  lpo: yup.string().required(errorMessages.browser.protocolSetupForm.lpo.required),
  protocolNumber: yup.string().required(errorMessages.browser.protocolSetupForm.protocolNumber.required),
  protocolTitle: yup.string().optional(),
  formPartType: yup
    .string()
    .oneOf(["OPEN_RAVE", "REGULATORY"], errorMessages.browser.protocolSetupForm.formPartType.required)
    .required(errorMessages.browser.protocolSetupForm.formPartType.required)
});

export const validateSetup = async (data, setError, clearErrors) => {
  const { lpo, protocolNumber, formPartType } = data;
  if (lpo && protocolNumber && formPartType) {
    const requestBody = {
      lpo,
      protocol: protocolNumber,
      formPartType
    };
    try {
      const response = await postWithAuth(requestValidationPath, requestBody);

      const activeRequest = response?.find(req =>
        !["COMPLETED", "CANCELED", "RETIRED", "INACTIVE"].includes(req.requestStatus)
      );

      if (activeRequest) {
        const setupType = formPartType === "OPEN_RAVE"
          ? errorMessages.browser.protocolSetupForm.labels.openRaveSetup
          : errorMessages.browser.protocolSetupForm.labels.regulatorySetup;

        const statusLabel = requestStatusOptions.find(
          option => option.value === activeRequest.requestStatus
        )?.label || activeRequest.requestStatus;

        setError("setupValidation", {
          type: "manual",
          message: errorMessages.browser.protocolSetupForm.validation.duplicate(setupType, statusLabel)
        });
      } else {
        clearErrors("setupValidation");
      }
    } catch (error) {
      setError("setupValidation", {
        type: "manual",
        message: errorMessages.browser.protocolSetupForm.validation.error
      });
    }
  }
};

const ProtocolSetupForm = ({ leadGroupLov, onClose }) => {
  const {
    control,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      lpo: '',
      protocolNumber: '',
      protocolTitle: "",
      formPartType: ""
    }
  });

  const history = useHistory();
  const selectedLpo = watch("lpo");
  const { protocolList } = useProtocolLOVs(selectedLpo || "");
  const { setProtocolSetupData } = useProtocolSetupData();
  const { setRequestBarData } = useRequestBarData();
  const canEditRegulatory = useSetupPermission(ProtectionElements.REGULATORY_REQUEST);
  const canEditOpenRave = useSetupPermission(ProtectionElements.OPEN_REQUEST);

  const formPartTypeOptions = [
    canEditOpenRave && {
      label: errorMessages.browser.protocolSetupForm.labels.openRaveSetup,
      value: "OPEN_RAVE"
    },
    canEditRegulatory && {
      label: errorMessages.browser.protocolSetupForm.labels.regulatorySetup,
      value: "REGULATORY"
    }
  ].filter(Boolean);


  const checkAndValidate = async () => {
    const data = getValues();
    const { lpo, protocolNumber, formPartType } = data;
    // Clear previous validation error first
    clearErrors("setupValidation");
    // Only run async validation if all required fields are present
    if (lpo && protocolNumber && formPartType) {
      await validateSetup(data, setError, clearErrors);
    }
  };

  const handleNext = () => {
    const data = getValues()
    setProtocolSetupData(data);
    setRequestBarData({
      id: data.protocolNumber,
      status: ''
    });
    if (data.formPartType === "OPEN_RAVE") {
      history.push({
        pathname: "/protocol-setup/open-rave"
      });
    } else {
      history.push({
        pathname: "/protocol-setup/regulatory"
      });
    }
  };

  const handleLpoChange = async () => {
    setValue("protocolNumber", "");
    setValue("formPartType", "");
    setValue("protocolTitle", "", { shouldValidate: true });
    clearErrors("setupValidation");
    // No need to validate here since protocolNumber and formPartType are cleared
  };

  const handleProtocolChange = async (selectedOption) => {
    clearErrors("setupValidation");
    if (selectedOption) {
      const protocolTitle = protocolList?.find((item) => item.value === selectedOption)?.label;
      setValue("protocolTitle", protocolTitle);
    } else {
      setValue("protocolTitle", "");
    }
    await checkAndValidate();
  };

  const handleFormPartTypeChange = async () => {
    clearErrors("setupValidation");
    await checkAndValidate();
  };

  return (
    <div className="form-layout-wrapper my-3">
      <div className="form-layout-header">
        <div className="form-layout-title invisible">{text.browser.protocolSetupForm.title}</div>
      </div>
      <form className="protocol-setup-form">
        <div className="form-layout-body p-0">
          <div className="invisible"></div>
          <ul>
            <li className="d-block">
              <FormSelect
                name="lpo"
                control={control}
                label={text.browser.protocolSetupForm.lpo.label}
                options={leadGroupLov}
                required
                placeholder="Select LPO"
                onChangeCallback={handleLpoChange}
                error={errors.lpo?.message}
                isClearable
              />
            </li>
            <li className="d-block">
              <FormSelect
                name="protocolNumber"
                control={control}
                label={text.browser.protocolSetupForm.protocolNumber.label}
                options={protocolList?.map((item) => ({
                  value: item.value,
                  label: item.value
                }))}
                required
                placeholder="Select protocol"
                onChangeCallback={handleProtocolChange}
                error={errors.protocolNumber?.message}
                isClearable
              />
              <div className="unset-before form-layout-message-tip p-1">
                {errorMessages.browser.protocolSetupForm.protocolNumber.helpText}
              </div>
            </li>
            <li className="d-block">
              <FormRadioGroup
                name="formPartType"
                control={control}
                label={text.browser.protocolSetupForm.formPartType.label}
                required
                onChangeCallBack={handleFormPartTypeChange}
                options={formPartTypeOptions}
                error={errors.formPartType?.message}
              />
            </li>
          </ul>
          {errors.setupValidation && (
            <div className="form-layout-message-error mt-1" role="alert">
              {errors.setupValidation.message}
            </div>
          )}
          <div className="d-flex justify-content-center mt-4 mb-n4">
            <button
              type="button"
              className="btn turquoise mr-2"
              onClick={() => handleNext()}
              disabled={!isValid}
              title={!isValid ? text.browser.protocolSetupForm.validation.required : text.browser.protocolSetupForm.validation.initiate}
            >
              {text.browser.protocolSetupForm.buttons.next}
              <svg
                aria-hidden="true"
                focusable="false"
                data-prefix="fas"
                data-icon="angle-right"
                className="svg-inline--fa fa-angle-right fa-w-14 mr-n1"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 256 512"
              >
                <path
                  fill="currentColor"
                  d="M224.3 273l-136 136c-9.4 9.4-24.6 9.4-33.9 0l-22.6-22.6c-9.4-9.4-9.4-24.6 0-33.9l96.4-96.4-96.4-96.4c-9.4-9.4-9.4-24.6 0-33.9L54.3 103c9.4-9.4 24.6-9.4 33.9 0l136 136c9.5 9.4 9.5 24.6 .1 34z"
                ></path>
              </svg>
            </button>
            <button type="button" className="btn close" onClick={onClose}>
              {text.browser.protocolSetupForm.buttons.cancel}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProtocolSetupForm;
